"use client";

import { IconArrowNarrowLeft, IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

type AuthStepHeaderProps = {
  title: string;
  /** Renders the back arrow; omit it on the first step. */
  onBack?: () => void;
  /**
   * Renders the close button. The dialog leaves it out because its own chrome
   * already draws one.
   */
  onClose?: () => void;
  className?: string;
};

/**
 * `modalHead` from the design: a 56px bar (72px from `md` up) with the title
 * centred between two 56px icon slots. The slots stay in place even when an
 * affordance is missing, so the title never shifts between steps.
 */
export const AuthStepHeader = ({
  title,
  onBack,
  onClose,
  className,
}: AuthStepHeaderProps) => {
  const t = useTranslations("auth");

  return (
    <div
      className={cn(
        "flex min-h-14 w-full items-center gap-3 md:min-h-18",
        className,
      )}
    >
      {onBack ? (
        <button
          aria-label={t("back")}
          className="flex size-14 shrink-0 items-center justify-center text-text"
          onClick={onBack}
          type="button"
        >
          <IconArrowNarrowLeft aria-hidden="true" className="size-6" />
        </button>
      ) : (
        <span aria-hidden="true" className="size-14 shrink-0" />
      )}

      <p className="flex-1 truncate text-center font-medium text-text text-xl leading-7">
        {title}
      </p>

      {onClose ? (
        <button
          aria-label={t("close")}
          className="flex size-14 shrink-0 items-center justify-center text-text"
          onClick={onClose}
          type="button"
        >
          <IconX aria-hidden="true" className="size-6" />
        </button>
      ) : (
        <span aria-hidden="true" className="size-14 shrink-0" />
      )}
    </div>
  );
};
